import React from 'react';
import { Edit, Trash2, Bell } from 'lucide-react';

export default function AdminProductCard({ product, onEdit, onDelete, onToggleSoldOut }) {
  const isOutOfStock = !product.is_sold_out && !product.is_by_order && (product.stock_quantity ?? 0) <= 0;
  const isUnavailable = product.is_sold_out || isOutOfStock;

  return (
    <div className={`bg-white p-4 rounded-lg border flex flex-col ${isUnavailable ? 'border-red-300' : 'border-gray-200'}`} data-testid={`admin-product-${product.id}`}>
      <div className="relative">
        <img
          src={product.images[0]}
          alt={product.name}
          className={`w-full h-48 object-cover rounded-lg mb-3 ${isUnavailable ? 'opacity-50' : ''}`}
          style={product.image_focal_points?.[0] ? { objectPosition: `${product.image_focal_points[0].x}% ${product.image_focal_points[0].y}%` } : undefined}
        />
        {product.is_sold_out && (
          <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold">SOLD OUT</div>
        )}
        {isOutOfStock && (
          <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold">OUT OF STOCK</div>
        )}
        {product.is_by_order && (
          <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">BY ORDER</div>
        )}
      </div>
      <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-1">{product.name}</h3>
      <p className="text-sm text-gray-600 mb-1">
        {product.category} • {product.metal_type} {product.metal_carat && `• ${product.metal_carat}`}
      </p>
      <p className="text-sm text-gray-600 mb-2">
        {product.model_category && `${product.model_category} • `}{product.weight}g • MC: {product.making_charges_percent}{product.making_charges_type === 'fixed' ? ' (Fixed)' : '%'}
      </p>
      <p className="text-xs text-gray-400 mb-2">
        {product.images.length} image{product.images.length !== 1 ? 's' : ''} • Stock: {product.stock_quantity ?? 0}
        {product.barcode && ` • Barcode: ${product.barcode}`}
        {product.gender?.length > 0 && ` • ${product.gender.join(', ')}`}
        {product.notify_users?.length > 0 && ` • ${product.notify_users.length} waiting for stock`}
      </p>
      <div className="h-6 mb-2">
        {product.is_featured && (
          <span className="inline-block px-2 py-1 bg-[#D4AF37] text-white text-xs rounded-full">Featured</span>
        )}
      </div>
      <div className="mt-auto space-y-2">
        <button
          onClick={() => onToggleSoldOut(product.id, isUnavailable)}
          className={`w-full px-3 py-2 rounded-sm text-sm transition-colors flex items-center justify-center space-x-1 ${
            isUnavailable ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-orange-500 text-white hover:bg-orange-600'
          }`}
          data-testid={`toggle-soldout-${product.id}`}
        >
          <Bell className="h-4 w-4" />
          <span>{isUnavailable ? 'Mark Available' : 'Mark Sold Out'}</span>
        </button>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(product)}
            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-sm text-sm hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
            data-testid={`edit-product-${product.id}`}
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </button>
          <button
            onClick={() => onDelete(product.id)}
            className="flex-1 bg-red-600 text-white px-3 py-2 rounded-sm text-sm hover:bg-red-700 transition-colors flex items-center justify-center space-x-1"
            data-testid={`delete-product-${product.id}`}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  );
}
